import { cacheKeys } from "@constants/cache-keys";
import { hubAxios } from "@helpers/lib/axios";
import { HUB_ENDPOINTS } from "@memorang/constants/urls";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";

type TipQueryArgs<TSchema extends z.ZodType> = {
	useCase: string;
	data: unknown;
	schema: TSchema;
	userId?: string | number;
	sessionId?: number;
	outputLanguage?: string;
	additionalInstructions?: string;
	enabled?: boolean;
	/**
	 * Compact, stable discriminator for the cache entry. Kept out of `data` so
	 * the (potentially large) payload isn't re-hashed into the query key on
	 * every render; changing it regenerates the tip when new practice lands.
	 */
	dataKey?: string | number;
};

/**
 * `staleTime: Infinity` keeps a tip stable while mounted. A non-2xx response
 * throws, which the callout treats as "hide the tip".
 */
export const tipQueryOptions = <TSchema extends z.ZodType>({
	useCase,
	data,
	schema,
	userId,
	sessionId,
	outputLanguage,
	additionalInstructions,
	enabled = true,
	dataKey,
}: TipQueryArgs<TSchema>) =>
	queryOptions({
		queryKey: [
			cacheKeys.tip,
			useCase,
			userId ?? null,
			sessionId ?? null,
			outputLanguage ?? null,
			dataKey ?? null,
		] as const,
		queryFn: async (): Promise<z.infer<TSchema>> => {
			const { data: response } = await hubAxios.post<unknown>(
				HUB_ENDPOINTS.TIPS,
				{
					useCase,
					data,
					jsonSchema: z.toJSONSchema(schema),
					...(additionalInstructions ? { additionalInstructions } : {}),
					...(sessionId ? { sessionId } : {}),
					...(outputLanguage ? { outputLanguage } : {}),
				},
			);
			return schema.parse(response);
		},
		enabled: enabled && data != null,
		staleTime: Number.POSITIVE_INFINITY,
		retry: 1,
	});
